import type { ReactNode } from 'react'

import { TONE_GLOSSARY } from '@aeris/status-meta'
import { Badge, Page, PageHeader, Section, type StatusTone } from '@aeris/ui'

import { FINANCE_LEGEND } from '../lib/finance-status-meta.tsx'
import { PURCHASE_LEGEND } from '../lib/purchasing-status-meta.tsx'
import { KIND_LABELS } from '../lib/sales-labels.ts'
import {
  onlyInLabel,
  STATUS_META,
  STATUSES_BY_KIND,
} from '../lib/sales-status-meta.tsx'

/**
 * O glossário — F156.
 *
 * A pergunta que originou tudo veio de um cliente da loja: *"convertido é o
 * quê? faturado o quê? quando fica verde e quando não fica?"*. O selo explica
 * um por vez, a legenda explica os daquela tela; **aqui está tudo, de uma vez,
 * e é o lugar para onde se manda alguém que está aprendendo o sistema**.
 *
 * ## Por que não tem permissão
 *
 * Está em `UNDENIABLE_PAGES`: o admin não consegue tirá-la de ninguém. Quem
 * tem menos telas é exatamente quem mais precisa da explicação, e uma ajuda que
 * se pode negar é uma ajuda que não está lá quando faz falta.
 *
 * ## A fonte é a mesma das telas
 *
 * Nada aqui é escrito à mão: as frases saem dos mesmos registros que desenham o
 * selo na lista. Um glossário com o seu próprio texto seria a quinta cópia do
 * vocabulário, e a primeira a envelhecer.
 */

const Entry = ({
  label,
  tone,
  icon,
  what,
  only,
}: {
  label: string
  tone: StatusTone
  icon?: ReactNode
  what: string
  only?: string | null
}): ReactNode => (
  <div className="flex flex-col gap-1 border-b border-(--border-subtle) py-3 last:border-b-0 sm:flex-row sm:gap-4">
    <div className="sm:w-48 sm:shrink-0">
      <Badge tone={tone} {...(icon === undefined ? {} : { icon })}>
        {label}
      </Badge>
      {only == null ? null : (
        <span className="ml-2 text-(length:--text-xs) text-(--text-muted)">
          {only}
        </span>
      )}
    </div>
    <p className="text-(length:--text-sm) text-(--text-secondary)">{what}</p>
  </div>
)

// O que cada cor quer dizer mora em `@aeris/status-meta` (F305), com a
// Ajuda do celular lendo o mesmo texto.
const COLOURS = TONE_GLOSSARY

export const AjudaPage = (): ReactNode => (
  <Page>
    <PageHeader
      title="Ajuda"
      description="O que cada situação quer dizer, e o que cada cor significa."
    />

    <Section
      title="As cores"
      description="A cor nunca é o único sinal: o nome está sempre escrito ao lado, e cada situação tem o seu ícone."
    >
      <div className="flex flex-col">
        {COLOURS.map((colour) => (
          <Entry
            key={colour.label}
            label={colour.label}
            tone={colour.tone}
            what={colour.what}
          />
        ))}
      </div>
    </Section>

    <Section
      title="Os três documentos de venda"
      description="Um orçamento vira pedido, e o pedido é o que sai da loja. A venda é a do balcão, que já nasce concluída."
    >
      <div className="flex flex-col">
        <Entry
          label={KIND_LABELS.quote}
          tone="neutral"
          what="Uma proposta. Ganha número quando é enviado, e pode segurar a mercadoria se for marcado para reservar."
        />
        <Entry
          label={KIND_LABELS.order}
          tone="neutral"
          what="Um compromisso de entregar. É dele que a mercadoria sai."
        />
        <Entry
          label={KIND_LABELS.sale}
          tone="neutral"
          what="A venda de balcão: quando é registrada, já acabou — o cliente levou e o dinheiro entrou."
        />
      </div>
    </Section>

    <Section
      title="As situações de um documento de venda"
      description="Nem toda situação existe em todo documento, e a ressalva ao lado diz onde cada uma aparece."
    >
      <div className="flex flex-col">
        {STATUSES_BY_KIND.quote
          .concat(
            STATUSES_BY_KIND.order.filter(
              (status) => !STATUSES_BY_KIND.quote.includes(status)
            ),
            STATUSES_BY_KIND.sale.filter(
              (status) =>
                !STATUSES_BY_KIND.quote.includes(status) &&
                !STATUSES_BY_KIND.order.includes(status)
            )
          )
          .map((status) => {
            const meta = STATUS_META[status]
            return (
              <Entry
                key={status}
                label={meta.label}
                tone={meta.tone}
                icon={meta.icon}
                what={
                  meta.next === undefined
                    ? meta.what
                    : `${meta.what} ${meta.next}`
                }
                only={onlyInLabel(meta, KIND_LABELS)}
              />
            )
          })}
      </div>
    </Section>

    <Section
      title="As situações de uma compra"
      description='"Confirmada" é amarela aqui e azul em Vendas de propósito: comprar confirmado é mercadoria que a loja espera, e é do fornecedor que se cobra.'
    >
      <div className="flex flex-col">
        {PURCHASE_LEGEND.map((meta) => (
          <Entry
            key={meta.label}
            label={meta.label}
            tone={meta.tone}
            icon={meta.icon}
            what={meta.what}
          />
        ))}
      </div>
    </Section>

    <Section
      title="As situações de um título"
      description="O atraso aparece no lugar da situação sem ser uma: ele é um fato sobre hoje, e por isso é contado em dias."
    >
      <div className="flex flex-col">
        {FINANCE_LEGEND.map((meta) => (
          <Entry
            key={meta.label}
            label={meta.label}
            tone={meta.tone}
            icon={meta.icon}
            what={meta.what}
          />
        ))}
      </div>
    </Section>
  </Page>
)
